import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DownloadPageSize, DropdownStatusList, FormTypeAdd, FormTypeEdit, PageSize, RentalStockStatusOptions, StatusCodeActive } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { GetStatus, money } from "utils/helpers"
import RevisionIcon from "assets/icon/RevisionIcon";
import { useModal } from "context/ModalContext";
import Select from 'react-select'
import { FilterCard, MainBodyCard, PagesTitle } from "components/Cards";
import Pagination from "components/Input/PaginationButton";
import SmallButton from "components/Button/SmallButton";
import { BrandService } from "services/BrandService/BrandService";
import { RentalStockService } from "services/RentalStockService/RentalRockService";
import GridEditButton from "components/Button/GridEditButton";
import GridDeleteButton from "components/Button/GridDeleteButton";
import DataTable from "components/DataTable";
import AddButton from "components/Button/AddButton";
import PrintButton from "components/Button/PrintButton";
import { exportRentalStockToPdf } from "utils/export/ExportStockRental";

export default function RentalStock() {
    let navigate = useNavigate()
    const firstLoad = useRef(true)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchBrandList } = BrandService()
    const { fetchRentalStockList, fetchDeleteRentalStock } = RentalStockService()

    const [productName, setProductName] = useState('')
    const [stockCode, setStockCode] = useState('')
    const [stockStatus, setStockStatus] = useState(RentalStockStatusOptions[0].value)
    const [brandOptions, setBrandOptions] = useState('')
    const [brand, setBrand] = useState('')
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [rentalStockList, setRentalStockList] = useState([])
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center",
        },
        {
            key: "stockCode",
            header: "Kode Stock"
        },
        {
            key: "productName",
            header: "Nama Item"
        },
        {
            key: "rentalPriceSession",
            header: "Harga Rental (Session)",
            render: (row) => 'Rp ' + money(row.rentalPriceSession),
        },
        {
            key: "rentalPriceDay",
            header: "Harga Rental (Daily)",
            render: (row) => 'Rp ' + money(row.rentalPriceDay),
        },
        {
            key: "sellingPrice",
            header: "Selling Price",
            render: (row) => 'Rp ' + money(row.sellingPrice),
        },
        {
            key: "stockStatus",
            header: "Stock Status",
            render: (row) => RentalStockStatusOptions.find((x) => x.value === row.stockStatus).label,
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.salesStockId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.salesStockId, row.status)} size="w-3" />
                </div>

            ),
        },
    ]

    // Button Event
    const addBtnClick = () => {
        navigate("/rental-stock/form?rentalStockId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (id) => {
        navigate("/rental-stock/form?rentalStockId=" + id + "&formType=" + FormTypeEdit)
    }
    const deleteBtnClick = async (id, status) => {
        const onConfirm = await confirm()
        if (!onConfirm) return

        showLoading()

        try {
            const res = await fetchDeleteRentalStock(id, status)
            if (!res) return

            showSuccess({
                onSuccess: () => {
                    handlePageChange(pageIndex)
                }
            })
        }
        finally {
            hideLoading()
        }
    }

    // Function Grid
    const handlePageChange = async (newIndex) => {
        const res = await fetchRentalStockList(productName, brand === null || brand === '' ? '' : brand.value, stockCode, stockStatus, status, PageSize, newIndex)
        if (!res) return

        const totalData = res?.data?.totalData ?? 0;
        const total = Math.max(1, Math.ceil(totalData / PageSize))

        setRentalStockList(res)
        setTotalPages(total)
        setPageIndex(newIndex)
    }

    useEffect(() => {
        const onLoad = async () => {
            showLoading()

            try {
                let res = await fetchBrandList('', '1', 10000, 0)
                if (!res) return

                let data = res?.data?.listData
                let arr = [{ label: 'All', value: '' }]

                data.map((data) => arr.push({ label: data.brandName, value: String(data.brandId) }))

                setBrandOptions(arr)

                await handlePageChange(pageIndex)
            }
            finally {
                hideLoading()
            }
        }

        onLoad()
    }, [brand, productName, stockStatus, status])

    const exportPdfBtnClick = async () => {
        showLoading()

        try {
            const res = await fetchRentalStockList(productName, brand === null || brand === '' ? '' : brand.value, stockCode, stockStatus, status, DownloadPageSize, 0)
            if (!res) return

            exportRentalStockToPdf(res?.data?.listData ?? [])
        }
        finally {
            hideLoading()
        }
    };

    return (
        <MainBodyCard>
            <PagesTitle title='Stock Rental' />

            <div className="flex flex-col sm:flex-row justify-between gap-8 mt-2">
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 w-full">
                    <FilterCard title='Brand' width='w-full sm:w-72'>
                        <Select className="w-full" options={brandOptions} value={brand} onChange={(e) => { setBrand(e) }} placeholder='All' />
                    </FilterCard>
                    <FilterCard title='Product Name' width='w-full sm:w-72'>
                        <input className="input" value={productName} onChange={(e) => { setProductName(e.target.value) }} />
                    </FilterCard>
                    <FilterCard title='Status' width='w-full sm:w-32'>
                        <Dropdown value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} placeholder='All' />
                    </FilterCard>
                </div>
            </div>

            <div className="flex w-full justify-between bg-white p-2 rounded-xl shadow-md text-sm border items-center mt-4">
                <div className="flex gap-2 flex-wrap">
                    {RentalStockStatusOptions.map((data, i) => {
                        return (
                            <div className={`border px-3 flex items-center rounded-full cursor-pointer ${stockStatus === data.value ? 'bg-black text-white' : ''} `}
                                onClick={() => { setStockStatus(data.value) }}
                            >
                                {data.label}
                            </div>
                        )
                    })}
                </div>
                <div className="flex gap-2">
                    <PrintButton
                        className="px-4 py-2 rounded-full border bg-white hover:bg-slate-100 whitespace-nowrap"
                        onClick={exportPdfBtnClick}
                    >
                        Export PDF
                    </PrintButton>
                    <AddButton onClick={addBtnClick} text='Tambah Stock' />
                </div>
            </div>

            {/* WIDE SCREEN */}
            <div className="mt-5 hidden sm:block">
                <DataTable
                    columns={columns}
                    data={rentalStockList?.data?.listData ?? []}
                />
            </div>

            {/* MOBILE SCREEN */}
            <div className="mt-2 overflow-hidden sm:border sm:rounded-3xl sm:shadow-sm sm:hidden">
                {rentalStockList?.data?.listData?.length === 0 && (
                    <div className="text-center h-12">
                        <span colSpan={8}>No Data Available</span>
                    </div>
                )}
                {rentalStockList?.data?.listData?.map((data, i) => {
                    return (
                        <div className="my-2 row border p-2 rounded-lg hover:bg-slate-100 flex justify-between items-center" key={i}>
                            <div className="flex flex-col">
                                <span>{data.brandName} {data.productName}</span>
                                <span>Rental per Hour: {money(data.rentalPriceHour)}</span>
                                <span>Rental per Session: {money(data.rentalPriceSession)}</span>
                                <span>Rental per Day: {money(data.rentalPriceDay)}</span>
                            </div>
                            <div className="flex gap-1">
                                <button className="bg-black p-2 rounded-lg gap-2 flex" onClick={() => editBtnClick(data.rentalStockId)}>
                                    <EditIcon className="w-3 fill-white" />
                                </button>
                                <button className={`bg-black p-2 rounded-lg flex gap-2`} onClick={() => deleteBtnClick(data.rentalStockId, data.status)}>
                                    {
                                        data.status === StatusCodeActive ?
                                            (<TrashIcon className="w-3 fill-white" />) :
                                            (<RevisionIcon className="w-3 fill-white" />)
                                    }
                                </button>
                            </div>
                        </div>
                        // <tr className="my-1 row h-10 border-y hover:bg-slate-100" key={ i }>
                        //     <td className="text-center">{ data.rowNumber }</td>
                        //     <td className="px-2">
                        //         <span>{ data.stockCode }</span>
                        //     </td>
                        //     <td className="px-2">
                        //         <span>{ data.brandName } { data.productName }</span>
                        //     </td>
                        //     <td className="px-2">
                        //         <span>Rp { money(data.rentalPriceHour) }</span>
                        //     </td>
                        //     <td className="px-2">
                        //         <span>Rp { money(data.sellingPrice) }</span>
                        //     </td>
                        //     <td className="px-2 hidden sm:table-cell">
                        //         <span>{ RentalStockStatusOptions.find((x) => x.value === data.stockStatus).label  }</span>
                        //     </td>
                        //     <td className="px-2 hidden sm:table-cell">
                        //         <span>{ GetStatus(data.status) }</span>
                        //     </td>
                        //     <td className="flex flex-col sm:flex-row justify-center py-2 gap-2 px-2">
                        //         <button className="button gap-2 flex" onClick={() => editBtnClick(data.rentalStockId)}>
                        //             <EditIcon className="w-3 fill-white" />
                        //         </button>
                        //         <button className={`button flex gap-2`} onClick={() => deleteBtnClick(data.rentalStockId, data.status)}>
                        //             {
                        //                 data.status === StatusCodeActive ? 
                        //                 (<TrashIcon className="w-3 fill-white" />) : 
                        //                 (<RevisionIcon className="w-3 fill-white" />)
                        //             }
                        //         </button>
                        //     </td>
                        // </tr>
                    )
                })}
            </div>

            <Pagination
                pageIndex={pageIndex}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
        </MainBodyCard>
    )
}

